import React from "react";
import { makeStyles, InputBase, IconButton } from "@material-ui/core";

interface TextInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  extraType?: "alfanumeric" | "alfanumericNoSpaces" | "numeric" | "text" | "plate";
  placeholder?: string;
  error?: string;
  description?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onRightIconClick?: () => void;
  disabled?: boolean;
  type?: string;
  inputStyle?: React.CSSProperties;
  className?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  inputRef?: React.Ref<HTMLInputElement>;
  name?: string;
  /** Por defecto "off": el guardia carga datos de otra persona, nunca los propios, así que el
   * autocompletado del navegador (y el falso positivo de "esto es un formulario de pago" que
   * dispara Google Pay/tarjetas guardadas en campos numéricos como "Número de remito") no aplica. */
  autoComplete?: string;
}

// Chrome ignora `autocomplete="off"` en los campos que su heurística clasifica como tarjeta o
// dirección (staging, por https, seguía ofreciendo tarjetas en "Número de remito"). En cambio un
// token que no reconoce deja al campo sin clasificar y no sugiere nada.
const NO_AUTOFILL_TOKEN = "nope";

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%",
  },
  label: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#364153",
    marginBottom: "4px",
  },
  requiredAsterisk: {
    color: "#EF4444",
  },
  inputWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "8px",
    borderRadius: "8px",
    border: "1px solid #E2E8F0",
    backgroundColor: "white",
    transition: "all 0.2s ease",
    "&:focus-within": {
      borderColor: "#2B7FFF",
      boxShadow: "0 0 0 3px rgba(43, 127, 255, 0.1)",
    },
  },
  inputWrapperError: {
    borderColor: "#EF4444",
    "&:focus-within": {
      borderColor: "#EF4444",
      boxShadow: "0 0 0 3px rgba(239, 68, 68, 0.1)",
    },
  },
  inputWrapperDisabled: {
    backgroundColor: "#F8FAFC",
    cursor: "not-allowed",
  },
  leftIconContainer: {
    display: "flex",
    alignItems: "center",
    color: "#64748B",
  },
  input: {
    flex: 1,
    fontSize: "16px",
    color: "#334155",
    "&::placeholder": {
      color: "#94A3B8",
      opacity: 1,
    },
  },
  rightIconContainer: {
    display: "flex",
    alignItems: "center",
    color: "#64748B",
  },
  iconButton: {
    padding: "4px",
    "&:hover": {
      backgroundColor: "#F1F5F9",
    },
  },
  helperText: {
    fontSize: "14px",
    marginTop: "4px",
  },
  errorText: {
    color: "#EF4444",
  },
  descriptionText: {
    color: "#64748B",
  },
}));

const TextInput = ({
  value,
  onChange,
  label,
  placeholder,
  error,
  description,
  leftIcon,
  rightIcon,
  onRightIconClick,
  disabled = false,
  inputStyle,
  type = "text",
  extraType,
  className,
  onKeyDown,
  onBlur,
  inputRef,
  name,
  autoComplete = "off",
}: TextInputProps) => {
  const classes = useStyles();

  // Truco anti-autofill de pago: Chrome ignora a propósito autoComplete="off" en campos que su
  // heurística clasifica como pago/dirección (ver comentario en TextInputProps más arriba), pero
  // nunca ofrece el chip de tarjetas guardadas sobre un input readOnly. Arrancamos en readOnly y lo
  // sacamos apenas el usuario hace foco de verdad, antes de que llegue a tipear, así el campo queda
  // editable de forma imperceptible y no se pierde ninguna funcionalidad.
  const [isReadOnly, setIsReadOnly] = React.useState(true);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Se saca de forma síncrona sobre el propio DOM (no sólo vía estado) para que quede sin
    // readOnly antes de que el navegador llegue a evaluar si ofrece el autocompletado.
    e.target.readOnly = false;
    setIsReadOnly(false);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Se vuelve a blindar para que el truco se repita en cada foco, no sólo en el primero.
    setIsReadOnly(true);
    onBlur?.(e);
  };

  const renderLabel = (labelText: string) => {
    const requiredMatch = labelText.match(/^(.*?)(\s*\*)$/);
    if (!requiredMatch) {
      return labelText;
    }

    return (
      <>
        {requiredMatch[1]}
        <span className={classes.requiredAsterisk}>{requiredMatch[2]}</span>
      </>
    );
  };

  const inputWrapperClasses = [
    classes.inputWrapper,
    error ? classes.inputWrapperError : "",
    disabled ? classes.inputWrapperDisabled : "",
  ]
    .filter(Boolean)
    .join(" ");

  const showHelper = !error && description;

  return (
    <div className={[classes.container, className].filter(Boolean).join(" ")}>
      {label && <label className={classes.label}>{renderLabel(label)}</label>}

      <div className={inputWrapperClasses}>
        {leftIcon && <div className={classes.leftIconContainer}>{leftIcon}</div>}

        <InputBase
          inputRef={inputRef}
          name={name}
          autoComplete={autoComplete === "off" ? NO_AUTOFILL_TOKEN : autoComplete}
          value={value}
          onChange={(e) => {
            let newValue = e.target.value;

            switch (extraType) {
              case "numeric":
                newValue = newValue.replace(/[^0-9]/g, "");
                break;

              case "text":
                newValue = newValue.replace(/[^A-Za-zÁÉÍÓÚáéíóúÑñ ]/g, "");
                break;

              case "alfanumeric":
                newValue = newValue.replace(/[^A-Za-zÁÉÍÓÚáéíóúÑñ0-9 ]/g, "");
                break;

              case "alfanumericNoSpaces":
                newValue = newValue.replace(/[^A-Za-zÁÉÍÓÚáéíóúÑñ0-9]/g, "");
                break;

              case "plate":
                newValue = newValue.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
                break;

              default:
                break;
            }

            // Sync the DOM value synchronously so filtered characters never
            // flash in the input before React re-renders with the new value.
            if (e.target.value !== newValue) {
              e.target.value = newValue;
            }

            onChange(newValue);
          }}
          placeholder={placeholder}
          disabled={disabled}
          type={type}
          className={classes.input}
          fullWidth
          style={inputStyle}
          onKeyDown={onKeyDown}
          readOnly={isReadOnly}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />

        {rightIcon && (
          <div className={classes.rightIconContainer}>
            {onRightIconClick ? (
              <IconButton
                onClick={onRightIconClick}
                disabled={disabled}
                className={classes.iconButton}
                size="small"
              >
                {rightIcon}
              </IconButton>
            ) : (
              rightIcon
            )}
          </div>
        )}
      </div>

      {error && <span className={`${classes.helperText} ${classes.errorText}`}>{error}</span>}

      {showHelper && (
        <span className={`${classes.helperText} ${classes.descriptionText}`}>{description}</span>
      )}
    </div>
  );
};

export default TextInput;
